import * as React from "react";
import { useState, useCallback } from "react";
import { useClientFilter } from "../../hooks/ClientFilterProvider";
import { GoToPagination } from "../GoToPagination";
import ClientTable from "./ClientTable";
import ClientsBulkUpdateModalForm from "./ClientsBulkUpdateModalForm";
import ReminderModalForm from "../reminder/ReminderModalForm";
import ReminderModel, { ReminderData, defaultReminderData } from "../../core/api/reminder/ReminderModel";
import EntityId from "../../core/api/EntityId";
import { useClientList } from "./hooks/useClientList";
import { ClientTableHeader } from "./components/ClientTableHeader";

const ROWS_PER_PAGE = 10;

interface ModalState {
  modifyClients: boolean;
  createReminders: boolean;
}

/**
 * Component that displays a list of clients with filtering and bulk actions
 */
export const ClientListTable: React.FC = () => {
  const clientFilterProvider = useClientFilter();
  const { clients, isLoading, totalCount, pagination, fetchClients } = useClientList();
  const [modalState, setModalState] = useState<ModalState>({
    modifyClients: false,
    createReminders: false,
  });

  const handleCreateBulkReminders = useCallback(async (values: ReminderData): Promise<EntityId> => {
    await ReminderModel.createBulk(values, clientFilterProvider.filter);
    return { id: 'bulk-create' } as EntityId;
  }, [clientFilterProvider.filter]);

  const handleModalClose = useCallback((modalType: keyof ModalState) => {
    setModalState(prev => ({ ...prev, [modalType]: false }));
  }, []);

  const handleModalOpen = useCallback((modalType: keyof ModalState) => {
    setModalState(prev => ({ ...prev, [modalType]: true }));
  }, []);

  const renderHeader = useCallback(() => (
    <ClientTableHeader
      onModifyClients={() => handleModalOpen('modifyClients')}
      onCreateReminders={() => handleModalOpen('createReminders')}
      myClients={clientFilterProvider.filter.myClients}
    />
  ), [clientFilterProvider.filter.myClients, handleModalOpen]);

  return (
    <div>
      <ClientTable
        customHeader={renderHeader()}
        clients={clients}
        isLoading={isLoading}
        pageSize={pagination.size}
        refresh={fetchClients}
        slots={{
          pagination: () => (
            <GoToPagination
              count={totalCount}
              rowsPerPageOptions={[ROWS_PER_PAGE]}
              pageSize={pagination.size}
            />
          ),
        }}
      />

      {modalState.modifyClients && (
        <ClientsBulkUpdateModalForm
          filter={clientFilterProvider.filter}
          refresh={fetchClients}
          close={() => handleModalClose('modifyClients')}
        />
      )}

      {modalState.createReminders && (
        <ReminderModalForm
          value={defaultReminderData}
          save={handleCreateBulkReminders}
          close={() => handleModalClose('createReminders')}
        />
      )}
    </div>
  );
};
